import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Page } from "../../analytics";
import { Button } from "../../Button";
import BotDetection from "../../components/BotDetection";
import { LOGO_ALT, TWILIO_SENDGRID_LOGO } from "../../constants";
import { signupContext } from "../../context";
import ErrorAlert from "../../ErrorAlert";
import { ExternalLink } from "../../external-navigate";
import { useFormState } from "../../hooks/useFormState";
import { TextInput } from "../../TextInput";
import { useSessionUsername, useUnifiedAuth } from "../../unified-auth";
import {
  idpAuthErrorMessage,
  ipAddressNotAllowed,
  teammateInviteAccountLimitReachedErrorMessage,
  teammateInviteEmailMismatchErrorMessage,
  teammateInviteInternalErrorMessage,
  teammateInviteInvalidTokenErrorMessage,
  teammateInviteUnauthorizedErrorMessage,
  teammateInviteUsernameNotAvailableErrorMessage,
  usernameNotFoundErrorMessage,
} from "./identifier";

export const teammateExistsErrorMessage = (
  <p style={{ margin: "0 10px" }}>
    This email is already associated with an existing SendGrid account. Please log in with your
    existing account credentials or use a different email address.
  </p>
);

interface PasswordProps {
  botDetectionEnabled: boolean;
  botDetectionSitekey: string;
  botDetectionToken?: string;
  botDetectionTimeoutMs?: number;
}

export default function Password({
  botDetectionEnabled,
  botDetectionSitekey,
  botDetectionToken = "",
  botDetectionTimeoutMs,
}: PasswordProps) {
  const forgotPasswordHost =
    `${import.meta.env.VITE_MAKO_HOST}/forgot_password` ||
    "https://app.sendgrid.com/forgot_password";
  const [sessionUsername] = useSessionUsername();
  const [username, setUsername, changeUsername] = useFormState(sessionUsername);
  const [password, , changePassword] = useFormState("");
  const {
    isLoading,
    ssoLoginInfo: ssoInfo,
    isSSOLogin,
    loginWithBasicAuth,
    error,
  } = useUnifiedAuth();
  const [errorBanner, setErrorBanner] = useState<string | Error | JSX.Element | undefined>("");
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState<string>(botDetectionToken);
  const [forceReload, setForceReload] = useState<number>(0);

  // Seeds the form from ?email= once on mount. Re-running when searchParams changes would overwrite
  // whatever the user has typed since.
  useEffect(() => {
    const email = searchParams.get("email") || "";
    if (email) {
      setUsername(email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setUsername]);

  useEffect(() => {
    if (searchParams.has("error") && searchParams.has("error_description")) {
      const errorDescription = searchParams.get("error_description") || "";
      setErrorBanner(errorDescription);
      reloadBotDetection();
    } else if (searchParams.has("error")) {
      const errorCode = searchParams.get("error") || "";
      const errorMessages = {
        ERR_USERNAME_NOT_FOUND: usernameNotFoundErrorMessage,
        ERR_TEAMMATE_INVITE_USERNAME_NOT_AVAILABLE: teammateInviteUsernameNotAvailableErrorMessage,
        ERR_TEAMMATE_INVITE_EMAIL_MISMATCH: teammateInviteEmailMismatchErrorMessage,
        ERR_TEAMMATE_INVITE_INVALID_TOKEN: teammateInviteInvalidTokenErrorMessage,
        ERR_TEAMMATE_INVITE_UNAUTHORIZED: teammateInviteUnauthorizedErrorMessage,
        ERR_TEAMMATE_INVITE_INTERNAL: teammateInviteInternalErrorMessage,
        ERR_TEAMMATE_INVITE_ACCOUNT_LIMIT_REACHED: teammateInviteAccountLimitReachedErrorMessage,
        ERR_IP_ADDRESS_NOT_ALLOWED: ipAddressNotAllowed,
        ERR_TEAMMATE_EXISTS: teammateExistsErrorMessage,
      } as const;

      const errorMessage =
        errorMessages[errorCode as keyof typeof errorMessages] || errorCode || idpAuthErrorMessage;
      setErrorBanner(errorMessage);
      reloadBotDetection();
    } else if (error) {
      setErrorBanner(error);
      reloadBotDetection();
      return;
    }
  }, [error, searchParams]);

  const reloadBotDetection = () => {
    // Reset bot detection token
    setToken("");
    setForceReload((prev) => prev + 1);
  };

  const formSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await loginWithBasicAuth({
      username: sanitizeUsername(username),
      password: password,
      ...(token && { bot_detection_token: token }),
    });
  };

  useEffect(() => {
    if (isSSOLogin) {
      const form = document.getElementById("ssoForm") as HTMLFormElement;
      form?.submit();
    }
  }, [isSSOLogin]);
  if (isSSOLogin) {
    return (
      <form
        id="ssoForm"
        method="post"
        name="frm"
        action={ssoInfo?.idp_url}
        style={{ display: "none" }}>
        <input type="hidden" name="SAMLRequest" value={ssoInfo?.saml_request} />
        <input type="hidden" name="RelayState" value={ssoInfo?.relay_state} />
        <input type="submit" />
      </form>
    );
  }

  const style = {
    marginTop: "10pt",
  };

  return (
    <>
      <main className="login">
        {errorBanner && <ErrorAlert input={errorBanner} />}
        <div className="login-container">
          <div data-role="login-modals" className="login-modals">
            <div className="login-modal">
              <div className="login-modal-table">
                <div className="login-modal-table-row">
                  <div className="login-modal-body">
                    <div className="login-logo">
                      <img alt={LOGO_ALT} src={TWILIO_SENDGRID_LOGO} width="246" height="72" />
                    </div>

                    <form data-role="login-form" className="login-form" onSubmit={formSubmit}>
                      <div
                        data-role="login-form-error-container"
                        className="hidden login-form-errors">
                        <div className="row">
                          <div className="col-8 col-start-3">
                            <span
                              className="login-form-error"
                              data-role="login-form-error-text"></span>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-8 col-start-3">
                          <TextInput
                            label="Username (this may be your email address)"
                            id="username"
                            value={username}
                            autoFocus={true}
                            autoComplete={"username"}
                            onChange={changeUsername}
                            containerRole={"usernameContainer"}
                          />
                        </div>
                      </div>
                      <div className="row login-username-options">
                        <div className="col-10">
                          <a className="login-link" href="/login/forgot-username">
                            Forgot your username?
                          </a>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-8 col-start-3">
                          <TextInput
                            label="Password"
                            id="password"
                            value={password}
                            type="password"
                            autoFocus={true}
                            onChange={changePassword}
                            containerRole={"passwordContainer"}
                          />
                        </div>
                      </div>
                      <div className="row login-password-options">
                        <div className="col-10">
                          <a
                            data-role="forgot-password-link"
                            data-faro-user-action-name="forgot-password-link"
                            className="login-link"
                            href={forgotPasswordHost}>
                            Forgot your password?
                          </a>
                        </div>
                      </div>
                      <div className="row" style={style}>
                        <div className="col-8 col-start-3">
                          {botDetectionEnabled && (
                            <BotDetection
                              siteKey={botDetectionSitekey}
                              onVerify={setToken}
                              onError={setErrorBanner}
                              timeoutMs={botDetectionTimeoutMs}
                              onTimeout={setToken}
                              page={Page.password}
                              key={`bot-detection-${forceReload}`}
                            />
                          )}
                        </div>
                      </div>

                      <div className="login-btn">
                        <Button
                          dataRole="continue-btn"
                          className="btn btn-primary"
                          isLoading={isLoading}
                          isDisabled={isLoading || (botDetectionEnabled && token.trim() === "")}>
                          Continue
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>

                <div className="login-modal-table-row">
                  <div className="login-modal-footer" data-role="login-modal-footer">
                    Don&apos;t have a Twilio SendGrid account?&nbsp;
                    <ExternalLink to={"/"} context={signupContext}>
                      Sign up now!
                    </ExternalLink>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

const sanitizeUsername = (username: string): string => username.trim();
